import React, { useState, useMemo, useCallback, useRef } from 'react';

import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { toPng } from 'html-to-image';
import { isEmpty } from 'lodash';
import { ArrowLeft } from 'lucide-react';
import Markdown from 'react-markdown';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import html2canvas from 'html2canvas-pro';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

import aiModel from 'src/assets/images/skincheck/ai-model.png';
import DialogTriangle from 'src/assets/images/skincheck/dialogue-triangle.svg';
import loadingWebm from 'src/assets/lotties/loading.webm';
import TakePhoto from 'src/components/camera/TakePhoto';
import RecommendationRoutine from 'src/components/skincheck-result/RecommendationRoutine';
import SkinElasticityLevel from 'src/components/skincheck-result/SkinElasticityLevel';
import SkinHydrationLevel from 'src/components/skincheck-result/SkinHydrationLevel';
import SkinOilinessLevel from 'src/components/skincheck-result/SkinOilinessLevel';
import SkinToneInfo, { getSkinToneData } from 'src/components/skincheck-result/SkinToneInfo';
import { CustomDot } from 'src/components/ui/custom-dot-carousel';
import { Image } from 'src/models/skincare';
import { AnalyticsService } from 'src/services/analytics.service';
import {
  getRecommendation,
  getSkinHealth,
  imageUrlToBase64,
  predictSkin,
  sendEmail,
} from 'src/services/skincheck/skincheck.service';
import useUserStore from 'src/stores/user';
// import { StorageKey } from 'src/constants';

// clean localstorage before running this file
localStorage.removeItem('skinMetric');

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
  },
};

type ImageSet = { acne: string; pigment: string; wrinkle: string };

export const Route = createFileRoute('/skincheck')({
  component: RouteComponent,
});

function RouteComponent() {
  const ref = useRef<HTMLDivElement>(null);
  const {
    user,
    // setUser
  } = useUserStore();
  const router = useRouter();

  const [_width, setWidth] = useState<number>(window.innerWidth);
  const [, setImages] = useState<{ front?: string; left?: string; right?: string }>({});
  const [step, setStep] = useState<'front' | 'left' | 'right' | 'done'>('front');
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [recommendation, setRecommendation] = useState<string>('Loading recommendation...');
  const [skinHealthRecommendation, setSkinHealthRecommendation] = useState<any>();
  const [startTime, setStartTime] = useState<number | null>(null);

  // This is for the set of images responses from API
  const [frontImageSet, setFrontImageSet] = useState<ImageSet>();
  const [leftImageSet, setLeftImageSet] = useState<ImageSet>();
  const [rightImageSet, setRightImageSet] = useState<ImageSet>();

  const [frontImageFull, setFrontImageFull] = useState<ImageSet>();
  const [leftImageFull, setLeftImageFull] = useState<ImageSet>();
  const [rightImageFull, setRightImageFull] = useState<ImageSet>();

  const [sequentialResults, setSequentialResults] = useState<{
    front?: any;
    left?: any;
    right?: any;
  }>({});
  // Set start time when component mounts
  React.useEffect(() => {
    setStartTime(Date.now());
  }, []);

  const handleSequentialAnalysis = useCallback((angle: 'front' | 'left' | 'right', data: any) => {
    setSequentialResults(prev => ({
      ...prev,
      [angle]: data,
    }));

    // Store image sets
    if (data?.skinCondition) {
      const imageSet = {
        acne: data.skinCondition.ance.imageUrl,
        pigment: data.skinCondition.pigmentation.imageUrl,
        wrinkle: data.skinCondition.wrinkle.imageUrl,
      };
      switch (angle) {
        case 'front':
          setFrontImageFull(imageSet);
          setFrontImageSet(imageSet);
          // toast('Front image analyzed! Please continue with left side', { type: 'success' });
          break;
        case 'left':
          setLeftImageFull(imageSet);
          setLeftImageSet(imageSet);
          // toast('Left image analyzed! Please continue with right side', { type: 'success' });
          break;
        case 'right':
          setRightImageFull(imageSet);
          setRightImageSet(imageSet);
          // toast('Right image analyzed! Click "Start Analyzing" to view results', {
          //   type: 'success',
          // });
          break;
      }
    }
  }, []);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }

  React.useEffect(() => {
    if (isEmpty(user)) {
      router.navigate({
        to: '/login',
      });
    }
  }, [user]);

  // React.useEffect(() => {
  //   try {
  //     // Load user data from localStorage
  //     const storedUser = localStorage.getItem(StorageKey.USER_INFO);
  //     console.log('Stored user:', storedUser);
  //     if (storedUser) {
  //       console.log('User found in localStorage:', storedUser);
  //       const parsedUser = JSON.parse(storedUser);
  //       setUser(parsedUser);
  //     } else {
  //       console.log('No user found in localStorage');
  //       router.navigate({
  //         to: '/login',
  //       });
  //     }
  //   } catch (error) {
  //     console.error('Error parsing user data from localStorage:', error);
  //     router.navigate({
  //       to: '/login',
  //     });
  //   }
  // }, []);

  React.useEffect(() => {
    const comingFromDevice = localStorage.getItem('comingFromDevicePage') === 'true';
    const skinCheckCompleted = localStorage.getItem('skinCheckCompleted') === 'true';

    if (comingFromDevice && skinCheckCompleted) {
      console.log('🔧 Device flow detected - using photos + device sensor data...');

      // Clear the flag to avoid affecting future visits
      localStorage.removeItem('comingFromDevicePage');

      // Skip the photo-taking UI and go directly to analysis
      setStep('done');
      setIsLoading(true);

      // Get stored images from localStorage
      const storedFrontImage = localStorage.getItem('frontFaceImage');
      const storedLeftImage = localStorage.getItem('leftFaceImage');
      const storedRightImage = localStorage.getItem('rightFaceImage');

      // Get device sensor data
      const skinMetric = localStorage.getItem('skinMetric');
      const faceMeasurements = localStorage.getItem('faceMeasurements');

      console.log('📸 Image check:', {
        front: storedFrontImage ? 'valid' : 'missing',
        left: storedLeftImage ? 'valid' : 'missing',
        right: storedRightImage ? 'valid' : 'missing',
      });

      console.log('📊 Sensor data check:', {
        skinMetric: skinMetric ? 'valid' : 'missing',
        faceMeasurements: faceMeasurements ? 'valid' : 'missing',
      });

      // Proceed if we have all required data
      if (
        storedFrontImage &&
        storedLeftImage &&
        storedRightImage &&
        skinMetric &&
        faceMeasurements
      ) {
        console.log('✅ All required data found - starting hybrid analysis');

        const deviceImages = {
          front: storedFrontImage,
          left: storedLeftImage,
          right: storedRightImage,
        };
        // Call the device + image analysis
        handleStartAnalyzingWithDeviceData(deviceImages, skinMetric, faceMeasurements);
      } else {
        console.log('❌ Missing required data - falling back to photo mode');
        setIsLoading(false);
        setStep('front');
      }
    } else {
      // Normal flow - start with photos
      console.log('Normal photo flow');
      window.addEventListener('resize', handleWindowSizeChange);
    }

    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, [startTime]);

  const handleStartAnalyzingWithDeviceData = async (
    images: { front: string; left: string; right: string },
    skinMetricString: string,
    faceMeasurementsString: string,
  ) => {
    console.log('🔄 Starting analysis with device sensor data...');

    try {
      const parsedSkinMetric = JSON.parse(skinMetricString);
      const parsedFaceMeasurements = JSON.parse(faceMeasurementsString);

      console.log('📊 Parsed device data:', { parsedSkinMetric, parsedFaceMeasurements });

      // Send the images to the server to perform the visual analysis
      const results = await Promise.all(
        Object.keys(images).map(async key => {
          const image = images[key as 'front' | 'left' | 'right'];

          try {
            console.log(`🔍 Analyzing ${key} image...`);

            // Predict skin for each image to get visual analysis
            const res = await predictSkin(image);
            if (!res.success || !res.data) {
              console.error(`Failed to analyze the ${key} image:`, res);
              toast(`Failed to analyze the ${key} image. Please try again.`, { type: 'error' });
              return null;
            }

            // Store the image sets for display
            const imageSet = {
              acne: res.data.skinCondition.ance.imageUrl as string,
              pigment: res.data.skinCondition.pigmentation.imageUrl as string,
              wrinkle: res.data.skinCondition.wrinkle.imageUrl as string,
            };

            switch (key) {
              case 'front':
                setFrontImageSet(imageSet);
                setFrontImageFull(imageSet);
                break;
              case 'left':
                setLeftImageSet(imageSet);
                setLeftImageFull(imageSet);
                break;
              case 'right':
                setRightImageSet(imageSet);
                setRightImageFull(imageSet);
                break;
            }

            return {
              type: key,
              data: res.data,
            };
          } catch (error) {
            console.error(`❌ Error analyzing ${key} image:`, error);
            return null;
          }
        }),
      );

      // Filter out null results and ensure we have valid data
      const filteredResults = results.filter((result): result is { type: string; data: any } => {
        return (
          result !== null &&
          result !== undefined &&
          result.data !== null &&
          result.data !== undefined
        );
      });

      if (filteredResults.length < 3) {
        console.error('❌ Not enough valid analysis results:', filteredResults.length);
        toast('Your images are not valid, please try again', { type: 'error' });
        setStep('front');
        setImages({});
        setIsLoading(false);
        return;
      }

      console.log('✅ Image analysis complete, setting results...');

      // Set the analysis results (now with device sensor data)
      setAnalysisResults(filteredResults);

      // Convert images to base64 for display
      handleConvertImagesToBase64(filteredResults);

      // Calculate averages using the device-enhanced data - now type-safe
      const avgSkinHealth = Math.floor(
        filteredResults.reduce((sum, item) => {
          return sum + (item.data?.skinHealth?.skinHealth || 5);
        }, 0) / filteredResults.length,
      );

      const avgHydration = Math.floor(
        filteredResults.reduce((sum, item) => {
          return sum + (item.data?.skinHealth?.hydration || 50);
        }, 0) / filteredResults.length,
      );

      const avgElasticity = Math.floor(
        filteredResults.reduce((sum, item) => {
          return sum + (item.data?.skinHealth?.elasticity || 50);
        }, 0) / filteredResults.length,
      );

      // use max elasticity instead of average
      const maxElasticity = Math.max(
        ...filteredResults.map(item => item.data?.skinHealth?.elasticity || 0),
      );

      const avgOiliness = Math.floor(
        filteredResults.reduce((sum, item) => {
          return sum + (item.data?.skinHealth?.oiliness || 50);
        }, 0) / filteredResults.length,
      );

      const avgSkinAge = Math.floor(
        filteredResults.reduce((sum, item) => {
          return sum + (item.data?.skinCondition?.skinAge || 30);
        }, 0) / filteredResults.length,
      );

      const avgSkinTone = Math.floor(
        filteredResults.reduce((sum, item) => {
          return sum + (item.data?.skinCondition?.skinTone?.value || 3);
        }, 0) / filteredResults.length,
      );

      // Find maximum values for visual conditions (from image analysis) - type-safe
      const maxAcneValue = Math.max(
        ...filteredResults.map(item => item.data?.skinCondition?.ance?.value || 1),
      );

      const maxPigmentationValue = Math.max(
        ...filteredResults.map(item => item.data?.skinCondition?.pigmentation?.value || 1),
      );

      const maxWrinkleValue = Math.max(
        ...filteredResults.map(item => item.data?.skinCondition?.wrinkle?.value || 1),
      );

      // Extract skin type from image analysis - type-safe
      const firstResult = filteredResults[0];
      if (!firstResult?.data?.skinType?.characteristics) {
        throw new Error('Missing skin type data in analysis results');
      }

      const skinTypeResult = firstResult.data.skinType.characteristics.map(
        (item: any) => item?.label || 'Normal',
      );
      const skinTypeCode = skinTypeResult
        .map((item: string) => item.charAt(0).toUpperCase())
        .join('');

      console.log('🔍 Final data preparation:');
      console.log('  - parsedSkinMetric:', parsedSkinMetric);
      console.log('  - avgHydration:', avgHydration);
      console.log('  - avgElasticity:', avgElasticity);
      console.log('  - maxElasticity:', maxElasticity);
      console.log('  - avgOiliness:', avgOiliness);

      // Prepare the API data combining image analysis + device sensor data
      const apiData = {
        wrinkles: maxWrinkleValue,
        skinType: skinTypeCode,
        skinHealth: avgSkinHealth,
        skinTone: avgSkinTone,
        hydration: avgHydration, 
        elasticity: maxElasticity, 
        oiliness: avgOiliness, 
        skinAge: avgSkinAge,
        ance: maxAcneValue,
        pigment: maxPigmentationValue,
        dataSensor: {
          hydration: parsedSkinMetric.hydration,
          elasticity: parsedSkinMetric.elasticity,
          oiliness: parsedSkinMetric.oiliness,
        },
      };

      console.log('📡 Final API data (image + device):', apiData);
      await handleLoadRecommendation(apiData);
      await handleLoadSkinHealthRecommendation(apiData);
      console.log('✅ Both API calls completed');

      // Track analytics
      if (startTime) {
        const totalTime = Math.round((Date.now() - startTime) / 1000);
        AnalyticsService.trackEvent('device_hybrid_analysis_completed', {
          totalTimeSeconds: totalTime,
          avgHydration,
          maxElasticity,
          avgOiliness,
          maxAcneValue,
          maxWrinkleValue,
        });
      }

      // Hide loading after processing
      setTimeout(() => {
        console.log('✅ Device hybrid analysis complete');
        setIsLoading(false);
      }, 1000);
    } catch (error) {
      console.error('❌ Error in device hybrid analysis:', error);
      Sentry.captureException(error);
      setIsLoading(false);
      toast('Error processing analysis, please try again', { type: 'error' });
      setStep('front');
    }
  };

  const overallSkinHealth = useMemo<string>(() => {
    if (!skinHealthRecommendation) return 'No data';
    switch (skinHealthRecommendation.skinHealth) {
      case 1:
        return 'Very Poor';
      case 2:
        return 'Poor';
      case 3:
        return 'Below Average';
      case 4:
        return 'Average';
      case 5:
        return 'Good';
      case 6:
        return 'Very Good';
      case 7:
        return 'Exceptional';
      default:
        return 'No data';
    }
  }, [skinHealthRecommendation]);

  const averageSkinAge = useMemo(() => {
    if (!analysisResults) return 0;
    const totalScore = analysisResults.reduce((acc: number, result: any) => {
      return acc + result.data.skinCondition.skinAge;
    }, 0);
    return Math.floor(totalScore / analysisResults.length);
  }, [analysisResults]);

  const averageSkinTone = useMemo(() => {
    if (!analysisResults) return 0;
    const totalScore = analysisResults.reduce((acc: number, result: any) => {
      return acc + result.data.skinCondition.skinTone.value;
    }, 0);
    return Math.floor(totalScore / analysisResults.length);
  }, [analysisResults]);

  const skinToneData = useMemo(() => {
    if (!analysisResults) return null;
    return getSkinToneData(averageSkinTone);
  }, [analysisResults, averageSkinTone]);

  const acneData = useMemo(() => {
    if (!analysisResults) return null;
    const maxLevel = analysisResults.reduce((acc: number, result: any) => {
      return Math.max(acc, result.data.skinCondition.ance.value);
    }, 0);
    const acneLevel = analysisResults.find(
      (item: any) => item.data.skinCondition.ance.value === maxLevel,
    );
    if (!acneLevel) return null;
    return acneLevel.data.skinCondition.ance;
  }, [analysisResults]);

  const pigmentationData = useMemo(() => {
    if (!analysisResults) return null;
    const maxLevel = analysisResults.reduce((acc: number, result: any) => {
      return Math.max(acc, result.data.skinCondition.pigmentation.value);
    }, 0);
    const pigmentationLevel = analysisResults.find(
      (item: any) => item.data.skinCondition.pigmentation.value === maxLevel,
    );
    if (!pigmentationLevel) return null;
    return pigmentationLevel.data.skinCondition.pigmentation;
  }, [analysisResults]);

  const wrinkleData = useMemo(() => {
    if (!analysisResults) return null;
    const maxLevel = analysisResults.reduce((acc: number, result: any) => {
      return Math.max(acc, result.data.skinCondition.wrinkle.value);
    }, 0);
    const wrinkleLevel = analysisResults.find(
      (item: any) => item.data.skinCondition.wrinkle.value === maxLevel,
    );
    if (!wrinkleLevel) return null;
    return wrinkleLevel.data.skinCondition.wrinkle;
  }, [analysisResults]);

  const skinTypeLabel = useMemo<string | null>(() => {
    // Determine the primary skin type label for the UI
    if (!analysisResults || !skinHealthRecommendation) return null;

    // Comment this out, so that align with app prediction. 
    // // Override the base AI image prediction if hydration is extreme
    // if (skinHealthRecommendation.hydration < 30) {
    //   return 'Dry';
    // } else if (skinHealthRecommendation.hydration > 70) {
    //   return 'Normal';
    // }
    
    // Otherwise, use the primary characteristic from the front image (index 0)
    return analysisResults[0].data.skinType.characteristics[0].label;
  }, [skinHealthRecommendation, analysisResults]);

  const skinType = useMemo(() => {
    // Generate the shorthand code (e.g. "OSPT") using the front image (index 0) characteristics
    if (!analysisResults) return '';
    const skinType = analysisResults[0].data.skinType.characteristics.map(
      (item: any) => item.label,
    );
    const skinTypeFirstCharacter = skinType.map((item: string) => item.charAt(0).toUpperCase());
    return skinTypeFirstCharacter.join('');
  }, [analysisResults]);

  const image: Image = {
    front: frontImageFull,
    left: leftImageFull,
    right: rightImageFull,
  };
  const dataOverview = {
    overview: {
      overall: overallSkinHealth,
      hydration: skinHealthRecommendation?.hydration,
      oiliness: skinHealthRecommendation?.oiliness,
      elasticity: skinHealthRecommendation?.elasticity,
      skinHealth: skinHealthRecommendation?.skinHealth,
      image: image,
      skinTone: {
        value: averageSkinTone,
        label: skinToneData?.label ?? 'No data',
      },
      skinAge: averageSkinAge,
      ance: {
        value: acneData?.value,
        label: acneData?.label ?? 'No data',
      },
      pigmentation: {
        value: pigmentationData?.value,
        label: pigmentationData?.label ?? 'No data',
      },
      wrinkle: {
        value: wrinkleData?.value,
        label: wrinkleData?.label ?? 'No data',
      },
    },
    yourSkinCharacteristics: {
      characteristics: [
        {
          label: analysisResults?.[0]?.data.skinType.characteristics?.[0]?.label ?? 'No data',
          icon: analysisResults?.[0]?.data.skinType.characteristics?.[0]?.icon ?? '',
        },
        {
          label: analysisResults?.[0]?.data.skinType.characteristics?.[1]?.label ?? 'No data',
          icon: analysisResults?.[0]?.data.skinType.characteristics?.[1]?.icon ?? '',
        },
      ],
      description: analysisResults?.[0]?.data.skinType.description ?? 'No data',
    },
    recommendations: recommendation,
  };

  const skinToneColor = useMemo(() => {
    if (!skinToneData) return '#FFFFFF';
    switch (skinToneData.value) {
      case 1:
        return '#e3cbbb';
      case 2:
        return '#e8c5a7';
      case 3:
        return '#ecc59b';
      case 4:
        return '#d6a87a';
      case 5:
        return '#b08b61';
      case 6:
        return '#886650';
      default:
        return '#FFFFFF';
    }
  }, [skinToneData]);

  const boxShadowAIModel = useMemo(() => {
    if (isLoading) return '';
    return 'shadow-[4px_6px_12px_0px_rgba(227,102,83,0.32)]';
  }, [isLoading]);

  const boxShadow = useMemo(() => {
    if (isLoading) return '';
    return 'shadow-[0px_12px_24px_0px_#0000001F]';
  }, [isLoading]);


  const filterCaptureNode = (node: HTMLElement) => {
    const exclusionClasses = ['remove-me'];
    return !exclusionClasses.some(classname => node.classList?.contains(classname));
  };

  // Handle capturing the page for email
  const handleCapturePage = async (email: string) => {
    toast('Preparing your email report...', { type: 'info' });
    const captureElement = document.getElementById('capture-result');
    const viewport = document.querySelector('meta[name="viewport"]');
    const bannerVideo = document.getElementById('banner-video') as HTMLVideoElement;

    if (!viewport || !bannerVideo || !captureElement) {
      toast('Failed to capture the page', { type: 'error' });
      Sentry.captureException('Viewport or banner video not found');
      console.error('Viewport or banner video not found', viewport, bannerVideo);
      return;
    }

    try {
      if (ref.current === null) {
        Sentry.captureException('Capture node not found');
        toast('Failed to capture the page', { type: 'error' });
        console.error('Capture node not found');
        return;
      }

      let dataUrl = '';

      if (_width < 1024) {
        try {
          const canvas = await html2canvas(captureElement, {
            scrollX: -window.scrollX,
            scrollY: -window.scrollY,
            windowWidth: 1280,
            windowHeight: document.documentElement.scrollHeight,
            scale: 1.5,
          });
          dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        } catch (error) {
          Sentry.captureException(error);
          console.error('Error capturing the page:', error);
          toast('Failed to capture the page' + JSON.stringify(error), { type: 'error' });
        }
      } else {
        dataUrl = await toPng(ref.current, {
          quality: 0.95,
          cacheBust: true,
          pixelRatio: 2,
          filter: filterCaptureNode,
        });
      }

      const data = {
        image: dataUrl,
        email: email,
      };
      const emailRes = await sendEmail(data);

      if (emailRes.success) {
        toast('Email sent successfully', { type: 'success' });
      } else {
        toast('Failed to send email', { type: 'error' });
        Sentry.captureException("ailed to send email from FE");
      }
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
    }
  };

  // Convert analysis images to base64 for display
  const handleConvertImagesToBase64 = async (data: any) => {
    try {
      const base64ImageSets = await Promise.all(
        data.map(async (item: any) => {
          const acneBase64 = await imageUrlToBase64(item.data.skinCondition.ance.imageUrl);
          const pigmentBase64 = await imageUrlToBase64(
            item.data.skinCondition.pigmentation.imageUrl,
          );
          const wrinkleBase64 = await imageUrlToBase64(item.data.skinCondition.wrinkle.imageUrl);

          return {
            type: item.type,
            acne: acneBase64,
            pigment: pigmentBase64,
            wrinkle: wrinkleBase64,
          };
        }),
      );

      base64ImageSets.forEach((imageSet: ImageSet & { type: string }) => {
        const set = {
          acne: imageSet.acne,
          pigment: imageSet.pigment,
          wrinkle: imageSet.wrinkle,
        };
        switch (imageSet.type) {
          case 'front':
            setFrontImageSet(set);
            break;
          case 'left':
            setLeftImageSet(set);
            break;
          case 'right':
            setRightImageSet(set);
            break;
          default:
            break;
        }
      });
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error converting images to base64:', e);
    }
  };

  // Handle image capture from TakePhoto component
  const handleCapture = useCallback(
    (angle: 'front' | 'left' | 'right', image: string | undefined) => {
      if (image) {
        setImages(prev => ({ ...prev, [angle]: image }));
      } else {
        setImages(prev => ({ ...prev, [angle]: undefined }));
      }
    },
    [],
  );

  // Load AI recommendation message
  const handleLoadRecommendation = async (apiData: any) => {
    console.log('🤖 Calling getRecommendation API with data:', JSON.stringify(apiData));

    try {
      const res = await getRecommendation(apiData);

      // Log the FULL response to see what's happening
      console.log('🤖 getRecommendation API full response:', JSON.stringify(res));
      console.log('🤖 Response success:', res.success);
      console.log('🤖 Response data:', res.data);

      if (res.success) {
        console.log('✅ Recommendation API successful');
        console.log('✅ Recommendation text:', res.data.recommend);
        setRecommendation(res.data.recommend);
      } else {
        console.error('❌ Recommendation API returned unsuccessful');
        console.error('❌ Error details:', res.error || res.message || 'No error details');
        setRecommendation('No recommendation available - API returned unsuccessful response');
      }
    } catch (error) {
      console.error('❌ Recommendation API threw an error:', error);
      Sentry.captureException("Recommendation API threw an error");
      setRecommendation('No recommendation available - API call failed');
    }
  };

  // Load skin health recommendation
  const handleLoadSkinHealthRecommendation = async (apiData: any) => {
    console.log('🏥 Calling getSkinHealth API with data:', JSON.stringify(apiData));

    try {
      const res = await getSkinHealth(apiData);

      console.log('🏥 getSkinHealth API full response:', JSON.stringify(res));
      console.log('🏥 Response success:', res.success);
      console.log('🏥 Response data:', res.data);

      if (res.success) {
        console.log('✅ SkinHealth API successful');
        setSkinHealthRecommendation(res.data.skinHealth);
      } else {
        console.error('❌ SkinHealth API returned unsuccessful');
        console.error('❌ Error details:', res.error || res.message || 'No error details');
        Sentry.captureException("SkinHealth API returned unsuccessful");
        setSkinHealthRecommendation(null);
      }
    } catch (error) {
      console.error('❌ SkinHealth API threw an error:', error);
      Sentry.captureException(error);
      setSkinHealthRecommendation(null);
    }
  };

  // Trigger analysis of all images - IMMEDIATE API CALL
  const triggerAnalysis = useCallback(async () => {
    // Check if we have all 3 results
    if (sequentialResults.front && sequentialResults.left && sequentialResults.right) {
      setIsLoading(true);

      try {
        // Process the data from sequential results
        const data = [sequentialResults.front, sequentialResults.left, sequentialResults.right];

        // Calculate average scores
        const avgSkinHealth = Math.floor(
          data.reduce((sum, item) => sum + item.skinHealth.skinHealth, 0) / data.length,
        );

        const avgHydration = Math.floor(
          data.reduce((sum, item) => sum + item.skinHealth.hydration, 0) / data.length,
        );

        const avgElasticity = Math.floor(
          data.reduce((sum, item) => sum + item.skinHealth.elasticity, 0) / data.length,
        );
        // use max elasticity instead of average
        const maxElasticity = Math.max(...data.map(item => item.skinHealth.elasticity));

        const avgOiliness = Math.floor(
          data.reduce((sum, item) => sum + item.skinHealth.oiliness, 0) / data.length,
        );

        const avgSkinAge = Math.floor(
          data.reduce((sum, item) => sum + item.skinCondition.skinAge, 0) / data.length,
        );

        const avgSkinTone = Math.floor(
          data.reduce((sum, item) => sum + item.skinCondition.skinTone.value, 0) / data.length,
        );

        // Find maximum values for certain conditions
        const maxAcneValue = Math.max(...data.map(item => item.skinCondition.ance.value));

        const maxPigmentationValue = Math.max(
          ...data.map(item => item.skinCondition.pigmentation.value),
        );

        const maxWrinkleValue = Math.max(...data.map(item => item.skinCondition.wrinkle.value));

        // Extract skin type
        const skinTypeResult = data[0].skinType.characteristics.map((item: any) => item.label);

        const skinTypeCode = skinTypeResult
          .map((item: string) => item.charAt(0).toUpperCase())
          .join('');

        // Load data from localStorage if exists
        const skinMetric = localStorage.getItem('skinMetric');
        let dataSensor = null;

        if (skinMetric) {
          const parsedSkinMetric = JSON.parse(skinMetric);
          dataSensor = {
            hydration: parsedSkinMetric.hydration,
            elasticity: parsedSkinMetric.elastic,
            oiliness: parsedSkinMetric.oily,
          };
        }

        console.log('🔍 Final data preparation:');
        console.log('  - dataSensor:', dataSensor);
        console.log('  - avgHydration:', avgHydration);
        console.log('  - avgElasticity:', avgElasticity);
        console.log('  - maxElasticity:', maxElasticity);
        console.log('  - avgOiliness:', avgOiliness);

        // Prepare the data for API
        const apiData = {
          wrinkles: maxWrinkleValue,
          skinType: skinTypeCode,
          skinHealth: avgSkinHealth,
          skinTone: avgSkinTone,
          hydration: avgHydration,
          elasticity: maxElasticity,
          oiliness: avgOiliness,
          skinAge: avgSkinAge,
          ance: maxAcneValue,
          pigment: maxPigmentationValue,
          dataSensor: dataSensor,
        };

        console.log('📡 Final API data (images only):', apiData);

        // Call the recommendation API asynchronously
        handleLoadRecommendation(apiData);

        // Call the skin health recommendation API
        await handleLoadSkinHealthRecommendation(apiData);

        // Set the rest of the state
        const combinedResults = [
          { type: 'front', data: sequentialResults.front },
          { type: 'left', data: sequentialResults.left },
          { type: 'right', data: sequentialResults.right },
        ];

        // Set combined results and move to display
        setAnalysisResults(combinedResults);
        setStep('done');

        // Track GA event for 3 selfies captured
        const totalTime = startTime ? Math.round((Date.now() - startTime) / 1000) : 0; // Convert to seconds
        AnalyticsService.trackEvent('3_selfies_captured', {
          totalTimeSeconds: totalTime,
        });

        // Process for display
        handleConvertImagesToBase64(combinedResults);
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error calling getRecommendation API:', error);
        toast('Failed to get recommendations', { type: 'error' });
      } finally {
        // Wait for 1 second before hiding the loading screen
        // For the images to fully load
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      }
    } else {
      toast('Not all images have been analyzed. Please wait for a moment and try again', {
        type: 'error',
      });
    }
  }, [sequentialResults]);

  // Render image sets for display
  const renderOverlayImageSet = (imageSet: ImageSet | undefined, label: string) => {
    if (!imageSet) return null;
    return (
      <div className="relative h-[320px] w-[320px] bg-gray-500">
        <img
          className="absolute top-0 left-0 aspect-square w-[320px] object-cover object-center"
          src={imageSet.acne}
          alt={`${label} acne analysis`}
        />
        <img
          className="absolute top-0 left-0 aspect-square w-[320px] object-cover object-center opacity-70"
          src={imageSet.pigment}
          alt={`${label} pigmentation analysis`}
        />
        <img
          className="absolute top-0 left-0 aspect-square w-[320px] object-cover object-center opacity-45"
          src={imageSet.wrinkle}
          alt={`${label} wrinkle analysis`}
        />
      </div>
    );
  };

  const renderImageSets = () => {
    return (
      <>
        {renderOverlayImageSet(frontImageSet, 'Front')}
        {renderOverlayImageSet(leftImageSet, 'Left')}
        {renderOverlayImageSet(rightImageSet, 'Right')}
      </>
    );
  };

  return (
    <div className="relative flex flex-col items-center justify-center">
      {/* Loading overlay */}
      <div
        className="absolute top-0 left-0 flex h-screen w-full flex-col items-center justify-center bg-black transition-all duration-300"
        style={{
          zIndex: isLoading ? 1000 : -1,
          opacity: isLoading ? 1 : 0,
        }}
      >
        <video id="banner-video" autoPlay muted playsInline loop className="h-[120px] w-[210px]">
          <source src={loadingWebm} type="video/webm" />
        </video>
      </div>

      {/* Photo taking view */}
      {step !== 'done' ? (
        <div className="container mx-auto max-w-[560px] bg-black">
          <TakePhoto
            setImage={handleCapture}
            onStartAnalysis={triggerAnalysis}
            onImageAnalyzed={handleSequentialAnalysis}
          />
        </div>
      ) : (
        /* Results view */
        <div
          id="capture-result"
          ref={ref}
          style={{
            height: isLoading ? '100vh' : 'auto',
            overflow: isLoading ? 'hidden' : 'auto',
          }}
          className="flex w-full flex-col items-center bg-white"
        >
          {/* Banner */}
          <div className="flex w-full items-center justify-center bg-cover bg-center py-[80px] xl:bg-[url(src/assets/images/skincheck/skincheck-banner.jpeg)]">
            <span>
              <span
                className="text-xl/[120%] font-normal lg:text-[2.5rem]/[120%]"
                style={{ color: '#FFFFFFB8' }}
              >
                Your
              </span>
              <span
                className="mx-2 text-2xl/[120%] font-bold lg:mx-3 lg:text-[3.125rem]/[120%]"
                style={{ color: '#FFFFFF' }}
              >
                Skin Health
              </span>
              <span
                className="text-xl/[120%] font-normal lg:text-[2.5rem]/[120%]"
                style={{ color: '#FFFFFF' }}
              >
                Assessment
              </span>
            </span>
          </div>

          {/* AI Recommendation Section */}
          <div className="relative my-12 flex w-full flex-col-reverse items-center justify-center lg:flex-row">
            <div
              data-html2canvas-ignore
              className="remove-me absolute -top-[36px] left-[16px] z-20 cursor-pointer lg:left-[calc(50%-512px)]"
            >
              <Link to="/" onClick={() => setImages({})}>
                <ArrowLeft color="#000" />
              </Link>
            </div>

            <img className="h-[128px] w-[128px]" src={aiModel} alt="AI Model" />
            <div
              className={`relative mb-6 w-[calc(100vw-32px)] rounded-[24px] px-6 py-4 lg:mb-0 lg:w-[31.25rem] ${boxShadowAIModel}`}
              style={{
                background: 'linear-gradient(64.18deg, #F7EDE2 0%, #ECE1F0 107.7%)',
              }}
            >
              <div className="absolute top-auto -bottom-[21px] left-[calc(50%-12px)] -rotate-90 lg:top-[calc(50%-12px)] lg:bottom-auto lg:-left-[21px] lg:rotate-0">
                <DialogTriangle />
              </div>

              <Markdown>{recommendation}</Markdown>
            </div>
          </div>

          {/* Analysis Results */}
          <div className="flex w-[calc(100vw-32px)] flex-col lg:max-w-[64rem]">
            {/* Desktop Image Sets */}
            <div className="hidden w-full flex-row items-center justify-between gap-0 overflow-auto lg:flex">
              {renderImageSets()}
            </div>

            {/* Mobile Image Carousel */}
            <div className="relative block lg:hidden">
              <Carousel
                responsive={responsive}
                showDots
                renderDotsOutside
                arrows={false}
                itemClass="mb-6"
                customDot={<CustomDot />}
              >
                <div className="relative flex h-[calc(100vw-32px)] w-[calc(100vw-32px)] flex-col items-center justify-center bg-gray-500">
                  {frontImageSet && (
                    <img
                      className="absolute top-0 left-0 aspect-square w-[calc(100vw-32px)] object-cover object-center"
                      src={frontImageSet.acne}
                      alt="Front acne analysis"
                    />
                  )}
                  {frontImageSet && (
                    <img
                      className="absolute top-0 left-0 aspect-square w-[calc(100vw-32px)] object-cover object-center opacity-70"
                      src={frontImageSet.pigment}
                      alt="Front pigmentation analysis"
                    />
                  )}
                  {frontImageSet && (
                    <img
                      className="absolute top-0 left-0 aspect-square w-[calc(100vw-32px)] object-cover object-center opacity-45"
                      src={frontImageSet.wrinkle}
                      alt="Front wrinkle analysis"
                    />
                  )}
                </div>
                <div className="relative flex h-[calc(100vw-32px)] w-[calc(100vw-32px)] flex-col items-center justify-center bg-gray-500">
                  {leftImageSet && (
                    <img
                      className="absolute top-0 left-0 aspect-square w-[calc(100vw-32px)] object-cover object-center"
                      src={leftImageSet.acne}
                      alt="Left acne analysis"
                    />
                  )}
                  {leftImageSet && (
                    <img
                      className="absolute top-0 left-0 aspect-square w-[calc(100vw-32px)] object-cover object-center opacity-70"
                      src={leftImageSet.acne}
                    />
                  )}
                  {leftImageSet && (
                    <img
                      className="absolute top-0 left-0 aspect-square w-[calc(100vw-32px)] object-cover object-center opacity-70"
                      src={leftImageSet.pigment}
                    />
                  )}
                  {leftImageSet && (
                    <img
                      className="absolute top-0 left-0 aspect-square w-[calc(100vw-32px)] object-cover object-center opacity-45"
                      src={leftImageSet.wrinkle}
                    />
                  )}
                </div>
                <div className="relative flex h-[calc(100vw-32px)] w-[calc(100vw-32px)] flex-col items-center justify-center bg-gray-500">
                  {rightImageSet && (
                    <img
                      className="absolute top-0 left-0 aspect-square w-[calc(100vw-32px)] object-cover object-center"
                      src={rightImageSet.acne}
                    />
                  )}
                  {rightImageSet && (
                    <img
                      className="absolute top-0 left-0 aspect-square w-[calc(100vw-32px)] object-cover object-center opacity-70"
                      src={rightImageSet.pigment}
                    />
                  )}
                  {rightImageSet && (
                    <img
                      className="absolute top-0 left-0 aspect-square w-[calc(100vw-32px)] object-cover object-center opacity-45"
                      src={rightImageSet.wrinkle}
                    />
                  )}
                </div>
              </Carousel>
            </div>

            <div
              className="mt-6 flex flex-row items-center justify-center px-3.5 py-3"
              style={{ background: 'linear-gradient(64.18deg, #F7EDE2 0%, #ECE1F0 107.7%)' }}
            >
              <div className="flex flex-row items-center">
                <div className="h-[10px] w-[10px] rounded-full" style={{ background: '#EE2C28' }} />
                <span className="ml-3 text-sm/[140%] font-semibold text-[#23262F]">
                  Acne/Blemish
                </span>
              </div>
              <div className="w-[48px]" />
              <div className="flex flex-row items-center">
                <div className="h-[10px] w-[10px] rounded-full" style={{ background: '#0FBE7B' }} />
                <span className="ml-3 text-sm/[140%] font-semibold text-[#23262F]">Pigment</span>
              </div>
              <div className="w-[48px]" />
              <div className="flex flex-row items-center">
                <div className="h-[10px] w-[10px] rounded-full" style={{ background: '#B973FF' }} />
                <span className="ml-3 text-sm/[140%] font-semibold text-[#23262F]">Wrinkles</span>
              </div>
            </div>

            <div className="mt-12 flex flex-col items-start justify-center lg:flex-row">
              <span className="w-full text-center text-xl/[120%] font-bold text-[#23262F] lg:w-[10.25rem] lg:text-left">
                Skin Health
              </span>
              <div className="mt-2 flex w-full flex-col lg:mt-0 lg:grid lg:w-auto lg:flex-1 lg:grid-cols-12 lg:gap-2">
                <div
                  className={`flex h-[5rem] w-full flex-col justify-between rounded-2xl border border-[#F4F4F4] p-3 lg:col-span-3 lg:w-auto ${boxShadow}`}
                >
                  <span className="text-sm/[140%] font-semibold text-[#353945]">Overall</span>
                  <span className="text-xl/[120%] font-bold text-[#23262F]">
                    {overallSkinHealth}
                  </span>
                </div>
                <div className="mt-2 lg:col-span-3 lg:mt-0">
                  <SkinHydrationLevel
                    score={skinHealthRecommendation ? skinHealthRecommendation.hydration : 0}
                  />
                </div>
                <div className="mt-2 lg:col-span-3 lg:mt-0">
                  <SkinOilinessLevel
                    score={skinHealthRecommendation ? skinHealthRecommendation.oiliness : 0}
                  />
                </div>
                <div className="mt-2 lg:col-span-3 lg:mt-0">
                  <SkinElasticityLevel
                    score={skinHealthRecommendation ? skinHealthRecommendation.elasticity : 0}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-col items-start justify-center lg:flex-row">
              <span className="w-full text-center text-xl/[120%] font-bold text-[#23262F] lg:w-[10.25rem] lg:text-left">
                Skin Condition
              </span>
              <div className="mt-2 flex flex-1 flex-row flex-wrap items-center gap-2 lg:mt-0 lg:flex-nowrap lg:gap-0">
                <div
                  className={`flex h-[7rem] w-[calc(50vw-20px)] flex-col rounded-2xl border border-[#F4F4F4] p-3 lg:w-auto lg:flex-1 ${boxShadow}`}
                >
                  <span className="text-sm/[140%] font-semibold text-[#353945]">Skin Age</span>
                  <span className="mt-3 text-[32px]/[100%] font-bold text-[#23262F]">
                    {averageSkinAge}
                  </span>
                  <span className="mt-[2px] text-sm/[140%] font-semibold text-[#353945]">
                    Years old
                  </span>
                </div>
                <div className="hidden w-[8px] lg:block" />
                <div
                  className={`flex h-[7rem] w-[calc(50vw-20px)] flex-col rounded-2xl border border-[#F4F4F4] p-3 lg:w-auto lg:flex-1 ${boxShadow}`}
                  style={{
                    background: skinToneColor,
                  }}
                >
                  <div className="flex flex-row items-center justify-between">
                    <span
                      className="text-sm/[140%] font-semibold"
                      style={{
                        color: skinToneData && skinToneData.value > 3 ? '#FFFFFF' : '#353945',
                      }}
                    >
                      Skin Tone
                    </span>
                    <SkinToneInfo skinTone={skinToneData} />
                  </div>

                  <span
                    className="mt-3 text-[32px]/[100%] font-bold"
                    style={{
                      color: skinToneData && skinToneData.value > 3 ? '#FFFFFF' : '#23262F',
                    }}
                  >
                    {skinToneData ? skinToneData.value : 'N/A'}
                  </span>
                  <span
                    className="mt-[2px] text-sm/[140%] font-semibold"
                    style={{
                      color: skinToneData && skinToneData.value > 3 ? '#FFFFFF' : '#353945',
                    }}
                  >
                    {skinToneData ? skinToneData.label : 'No data'}
                  </span>
                </div>

                <div className="hidden w-[8px] lg:block" />
                <div
                  className={`flex h-[7rem] w-[calc((100vw-48px)/3)] flex-col rounded-2xl border border-[#F4F4F4] p-3 lg:w-auto lg:flex-1 ${boxShadow}`}
                >
                  <span className="text-sm/[140%] font-semibold text-[#353945]">Acne Level</span>
                  <span className="mt-3 text-[32px]/[100%] font-bold text-[#23262F]">
                    {acneData ? acneData.value : 'N/A'}
                  </span>
                  <span className="mt-[2px] text-sm/[140%] font-semibold text-[#353945]">
                    {acneData ? acneData.label : 'No data'}
                  </span>
                </div>
                <div className="hidden w-[8px] lg:block" />
                <div
                  className={`flex h-[7rem] w-[calc((100vw-48px)/3)] flex-col rounded-2xl border border-[#F4F4F4] p-3 lg:w-auto lg:flex-1 ${boxShadow}`}
                >
                  <span className="text-sm/[140%] font-semibold text-[#353945]">Pigmentation</span>
                  <span className="mt-3 text-[32px]/[100%] font-bold text-[#23262F]">
                    {pigmentationData ? pigmentationData.value : 'N/A'}
                  </span>
                  <span className="mt-[2px] text-sm/[140%] font-semibold text-[#353945]">
                    {pigmentationData ? pigmentationData.label : 'No data'}
                  </span>
                </div>
                <div className="hidden w-[8px] lg:block" />
                <div
                  className={`flex h-[7rem] w-[calc((100vw-48px)/3)] flex-col rounded-2xl border border-[#F4F4F4] p-3 lg:w-auto lg:flex-1 ${boxShadow}`}
                >
                  <span className="text-sm/[140%] font-semibold text-[#353945]">Wrinkle Level</span>
                  <span className="mt-3 text-[32px]/[100%] font-bold text-[#23262F]">
                    {wrinkleData ? wrinkleData.value : 'N/A'}
                  </span>
                  <span className="mt-[2px] text-sm/[140%] font-semibold text-[#353945]">
                    {wrinkleData ? wrinkleData.label : 'No data'}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-col items-start justify-center lg:flex-row">
              <span className="w-full text-center text-xl/[120%] font-bold text-[#23262F] lg:w-[10.25rem] lg:text-left">
                Skin Type
              </span>
              <div className="mt-2 flex flex-1 flex-row flex-wrap gap-2 lg:mt-0 lg:grid lg:grid-cols-12">
                <div
                  className={`flex h-[5rem] w-[calc(50vw-20px)] flex-col items-center justify-center rounded-2xl border border-[#F4F4F4] p-3 lg:col-span-3 lg:w-auto ${boxShadow}`}
                >
                  <span className="text-lg/[140%] font-bold text-[#23262F]">
                    {skinTypeLabel ? skinTypeLabel : 'No data'}
                  </span>
                </div>
                <div
                  className={`flex h-[5rem] w-[calc(50vw-20px)] flex-col items-center justify-center rounded-2xl border border-[#F4F4F4] p-3 lg:col-span-3 lg:w-auto ${boxShadow}`}
                >
                  <span className="text-lg/[140%] font-bold text-[#23262F]">
                    {analysisResults
                      ? analysisResults[0].data.skinType.characteristics[1].label
                      : 'No data'}
                  </span>
                </div>
                <div
                  className={`flex h-[5rem] w-[calc(100vw-32px)] flex-col items-center justify-center rounded-2xl border border-[#F4F4F4] p-3 lg:col-span-6 lg:w-auto ${boxShadow}`}
                >
                  <span className="text-sm/[140%] font-normal text-[#23262F]">
                    {analysisResults ? analysisResults[0].data.skinType.description : 'No data'}
                  </span>
                </div>
              </div>
            </div>

            <RecommendationRoutine
              isLoading={isLoading}
              handleCapturePage={handleCapturePage}
              setLoading={setIsLoading}
              wrinkleScore={wrinkleData ? wrinkleData.value : 0}
              skinType={skinType}
              skinHealthScore={skinHealthRecommendation ? skinHealthRecommendation.skinHealth : 0}
              skinTone={averageSkinTone}
              hydrationScore={skinHealthRecommendation ? skinHealthRecommendation.hydration : 0}
              elasticityScore={skinHealthRecommendation ? skinHealthRecommendation.elasticity : 0}
              oilinessScore={skinHealthRecommendation ? skinHealthRecommendation.oiliness : 0}
              skinAge={averageSkinAge}
              anceScore={acneData ? acneData.value : 0}
              pigmentationScore={pigmentationData ? pigmentationData.value : 0}
              dataInfo={dataOverview}
            />
          </div>
        </div>
      )}
    </div>
  );
}
